import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CalendarDays, MapPin, Users, Sparkles, CalendarPlus, Download } from "lucide-react";
import { api } from "../lib/api";

// Assume events run 4 hours by default (typical concert/gala length)
const DEFAULT_EVENT_HOURS = 4;

function fmtGCalDate(iso) {
  // Google Calendar expects YYYYMMDDTHHMMSSZ (UTC, basic ISO)
  return iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function googleCalendarUrl(event) {
  const start = new Date(event.event_date);
  const end = new Date(start.getTime() + DEFAULT_EVENT_HOURS * 60 * 60 * 1000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${fmtGCalDate(start.toISOString())}/${fmtGCalDate(end.toISOString())}`,
    location: event.location || "",
    details: `${event.description || ""}\n\nBook a chauffeured ride with Plenty Rydez.`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function icsFileContent(event) {
  const start = new Date(event.event_date);
  const end = new Date(start.getTime() + DEFAULT_EVENT_HOURS * 60 * 60 * 1000);
  const dt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const esc = (s) => (s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Plenty Rydez//Event//EN",
    "BEGIN:VEVENT",
    `UID:${event.id}@plenty-rydez.com`,
    `DTSTAMP:${dt(new Date())}`,
    `DTSTART:${dt(start)}`,
    `DTEND:${dt(end)}`,
    `SUMMARY:${esc(event.title)}`,
    `LOCATION:${esc(event.location || "")}`,
    `DESCRIPTION:${esc(event.description || "")}\\n\\nBook a chauffeured ride with Plenty Rydez.`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

function downloadIcs(event) {
  const blob = new Blob([icsFileContent(event)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(event.title || "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/events/${eventId}/chauffeur-vehicles`)
      .then((r) => setData(r.data))
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [eventId, navigate]);

  const groups = useMemo(() => {
    if (!data?.vehicles) return { sprinters: [], luxury: [] };
    const sprinters = data.vehicles.filter((v) =>
      (v.category || "").toLowerCase().includes("van") ||
      (v.name || "").toLowerCase().includes("sprinter")
    );
    // sort sprinters by capacity ascending so the list reads 8 → 10 → 14
    sprinters.sort((a, b) => (a.capacity || 0) - (b.capacity || 0));
    const luxury = data.vehicles.filter((v) => !sprinters.includes(v));
    return { sprinters, luxury };
  }, [data]);

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-cream border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { event } = data;

  return (
    <section data-testid="event-detail-page" className="mx-auto max-w-6xl px-5 sm:px-8 py-12">
      <Link to="/" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> Back to home
      </Link>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 mb-14">
        <div className="rounded-3xl overflow-hidden border border-white/10 bg-ink-800">
          {event.flyer_url ? (
            <img src={event.flyer_url} alt={event.title}
              data-testid="event-flyer" className="w-full aspect-[3/4] object-cover" />
          ) : (
            <div className="aspect-[3/4] flex items-center justify-center text-white/20">
              <CalendarDays size={64} />
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div>
            <div className="label-caps text-gold mb-2">{event.category || "event"}</div>
            <h1 data-testid="event-title" className="font-serif text-4xl sm:text-5xl leading-tight">{event.title}</h1>
          </div>

          <div className="glass rounded-2xl p-5 space-y-3">
            <div className="flex items-start gap-3">
              <CalendarDays size={16} className="text-gold mt-0.5 flex-shrink-0" />
              <div>
                <div className="label-caps text-white/40 text-[10px]">Date & time</div>
                <div className="text-sm">{new Date(event.event_date).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</div>
              </div>
            </div>
            {event.location && (
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-gold mt-0.5 flex-shrink-0" />
                <div>
                  <div className="label-caps text-white/40 text-[10px]">Location</div>
                  <div className="text-sm">{event.location}</div>
                </div>
              </div>
            )}
          </div>

          {event.description && (
            <p className="text-white/70 leading-relaxed">{event.description}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <a
              data-testid="event-add-gcal"
              href={googleCalendarUrl(event)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs label-caps bg-cream text-ink-900 hover:brightness-105 transition font-semibold"
            >
              <CalendarPlus size={14} /> Add to Google Calendar
            </a>
            <button
              type="button"
              data-testid="event-add-ics"
              onClick={() => downloadIcs(event)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs label-caps border border-cream/30 text-cream hover:bg-cream/10 transition"
            >
              <Download size={14} /> Apple / Outlook (.ics)
            </button>
          </div>

          <div className="glass rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <Sparkles size={16} className="text-gold mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="label-caps text-gold text-[10px] mb-1">Chauffeured only</div>
                <div className="text-white/70">Sit back and enjoy. Below is every vehicle from our fleet that can be driven for you to this event.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sprinter vans — passenger counts prominent */}
      {groups.sprinters.length > 0 && (
        <div className="mb-12" data-testid="event-sprinters">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="label-caps text-gold mb-1">Group rides · Sprinter vans</div>
              <h2 className="font-serif text-2xl sm:text-3xl">Bring the whole crew</h2>
            </div>
            <div className="text-xs text-white/50">Passenger count shown on each option</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {groups.sprinters.map((v) => (
              <SprinterCard key={v.id} vehicle={v} eventId={event.id} />
            ))}
          </div>
        </div>
      )}

      {/* Luxury chauffeured vehicles */}
      {groups.luxury.length > 0 && (
        <div data-testid="event-luxury">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="label-caps text-gold mb-1">Luxury chauffeured</div>
              <h2 className="font-serif text-2xl sm:text-3xl">Or ride solo in a Maybach</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {groups.luxury.map((v) => (
              <ChauffeurCard key={v.id} vehicle={v} eventId={event.id} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function SprinterCard({ vehicle, eventId }) {
  const cap = vehicle.capacity || 0;
  return (
    <Link
      to={`/book/ride/${vehicle.id}?event=${eventId}`}
      data-testid={`event-sprinter-${vehicle.id}`}
      className="group relative rounded-2xl border border-gold/30 hover:border-gold transition-colors bg-ink-800/70 overflow-hidden"
    >
      <div className="aspect-[16/10] bg-ink-700 overflow-hidden">
        <img src={vehicle.image_url} alt={vehicle.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1400&q=80"; }} />
        {/* Passenger badge — dominant */}
        <div className="absolute top-3 left-3 bg-gold text-ink-900 rounded-2xl px-4 py-2 shadow-[0_0_20px_rgba(212,175,55,0.55)] font-bold inline-flex items-center gap-2">
          <Users size={18} strokeWidth={2.5} />
          <span className="font-serif text-2xl leading-none">{cap}</span>
          <span className="label-caps text-[10px] leading-tight">seats</span>
        </div>
      </div>
      <div className="p-4">
        <div className="font-serif text-lg text-white leading-tight mb-1">{vehicle.name}</div>
        <div className="text-xs text-white/60 flex items-center gap-1 mb-3">
          <Users size={11} className="text-gold" />
          <span data-testid={`event-sprinter-cap-${vehicle.id}`}>Fits {cap} passenger{cap === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-end justify-between">
          <div className="text-xs text-white/50">
            <span className="text-gold font-semibold text-base">${(vehicle.hourly_rate || 0).toFixed(0)}</span>/hr chauffeured
          </div>
          <span className="btn-cream px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1">
            Book <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function ChauffeurCard({ vehicle, eventId }) {
  return (
    <Link
      to={`/book/ride/${vehicle.id}?event=${eventId}`}
      data-testid={`event-luxury-${vehicle.id}`}
      className="group relative rounded-2xl border border-white/10 hover:border-cream/50 transition-colors bg-ink-800/70 overflow-hidden"
    >
      <div className="aspect-[16/10] bg-ink-700 overflow-hidden">
        <img src={vehicle.image_url} alt={vehicle.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1400&q=80"; }} />
      </div>
      <div className="p-4">
        <div className="label-caps text-white/40 text-[10px] mb-1">{vehicle.category?.replace(/-/g, " ")}</div>
        <div className="font-serif text-lg text-white leading-tight mb-2">{vehicle.name}</div>
        <div className="text-xs text-white/60 flex items-center gap-3 mb-3">
          <span className="inline-flex items-center gap-1"><Users size={11} className="text-cream" /> {vehicle.capacity}</span>
        </div>
        <div className="flex items-end justify-between">
          <div className="text-xs text-white/50">
            <span className="text-cream font-semibold text-base">${(vehicle.hourly_rate || 0).toFixed(0)}</span>/hr
          </div>
          <span className="btn-cream px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1">
            Book <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}
